var {
  usePress
} = require("@react-aria/interactions");

var {
  useFocusable
} = require("@react-aria/focus");

var {
  useCollator,
  useLocale,
  useMessageFormatter
} = require("@react-aria/i18n");

var {
  useMemo
} = require("react");

var {
  mergeProps,
  useDescription,
  useId,
  useUpdateEffect,
  isAndroid
} = require("@react-aria/utils");

var {
  announce
} = require("@react-aria/live-announcer");

var _babelRuntimeHelpersInteropRequireDefault = $parcel$interopDefault(require("@babel/runtime/helpers/interopRequireDefault"));

var _babelRuntimeHelpersExtends = $parcel$interopDefault(require("@babel/runtime/helpers/extends"));

var {
  useGridRowGroup,
  useGrid,
  GridKeyboardDelegate,
  useGridCell,
  useGridRow,
  useGridSelectionCheckbox
} = require("@react-aria/grid");

exports.useTableRowGroup = useGridRowGroup;

function $parcel$interopDefault(a) {
  return a && a.__esModule ? a.default : a;
}

/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
const $b62c6c69dd91ba8b8df7cfb5ba576$export$gridIds = new WeakMap();

function $b62c6c69dd91ba8b8df7cfb5ba576$var$normalizeKey(key) {
  if (typeof key === 'string') {
    return key.replace(/\s*/g, '');
  }

  return '' + key;
}

function $b62c6c69dd91ba8b8df7cfb5ba576$export$getColumnHeaderId(state, columnKey) {
  let gridId = $b62c6c69dd91ba8b8df7cfb5ba576$export$gridIds.get(state);

  if (!gridId) {
    throw new Error('Unknown grid');
  }

  return gridId + "-" + $b62c6c69dd91ba8b8df7cfb5ba576$var$normalizeKey(columnKey);
}

function $b62c6c69dd91ba8b8df7cfb5ba576$export$getCellId(state, rowKey, columnKey) {
  let gridId = $b62c6c69dd91ba8b8df7cfb5ba576$export$gridIds.get(state);

  if (!gridId) {
    throw new Error('Unknown grid');
  }

  return gridId + "-" + $b62c6c69dd91ba8b8df7cfb5ba576$var$normalizeKey(rowKey) + "-" + $b62c6c69dd91ba8b8df7cfb5ba576$var$normalizeKey(columnKey);
}

function $b62c6c69dd91ba8b8df7cfb5ba576$export$getRowLabelledBy(state, rowKey) {
  // A row is labelled by it's row headers.
  return [...state.collection.rowHeaderColumnKeys].map(columnKey => $b62c6c69dd91ba8b8df7cfb5ba576$export$getCellId(state, rowKey, columnKey)).join(' ');
}

class $d19e2fa944f991da21b1694810eb968d$export$TableKeyboardDelegate extends GridKeyboardDelegate {
  isCell(node) {
    return node.type === 'cell' || node.type === 'rowheader' || node.type === 'column';
  }

  getKeyBelow(key) {
    let startItem = this.collection.getItem(key);

    if (!startItem) {
      return;
    } // If focus was on a column, then focus the first child column if any,
    // or find the corresponding cell in the first row.


    if (startItem.type === 'column') {
      let child = [...startItem.childNodes][0];

      if (child) {
        return child.key;
      }

      let firstItem = this.collection.getItem(this.getFirstKey());
      return [...firstItem.childNodes][startItem.index].key;
    }

    return super.getKeyBelow(key);
  }

  getKeyAbove(key) {
    let startItem = this.collection.getItem(key);

    if (!startItem) {
      return;
    } // If focus was on a column, focus the parent column if any


    if (startItem.type === 'column') {
      let parent = this.collection.getItem(startItem.parentKey);

      if (parent && parent.type === 'column') {
        return parent.key;
      }

      return;
    } // only return above row key if not header row


    let superKey = super.getKeyAbove(key);

    if (superKey && this.collection.getItem(superKey).type !== 'headerrow') {
      return superKey;
    } // If no item was found, and focus was on a cell, then focus the
    // corresponding column header.


    if (this.isCell(startItem)) {
      return this.collection.columns[startItem.index].key;
    } // If focus was on a row, then focus the first column header.


    return this.collection.columns[0].key;
  }

  findNextColumnKey(column) {
    // Search following columns
    let key = this.findNextKey(column.key, item => item.type === 'column');

    if (key != null) {
      return key;
    } // Wrap around to the first column


    let row = this.collection.headerRows[column.level];

    for (let item of row.childNodes) {
      if (item.type === 'column') {
        return item.key;
      }
    }
  }

  findPreviousColumnKey(column) {
    // Search previous columns
    let key = this.findPreviousKey(column.key, item => item.type === 'column');

    if (key != null) {
      return key;
    } // Wrap around to the last column


    let row = this.collection.headerRows[column.level];
    let childNodes = [...row.childNodes];

    for (let i = childNodes.length - 1; i >= 0; i--) {
      let item = childNodes[i];

      if (item.type === 'column') {
        return item.key;
      }
    }
  }

  getKeyRightOf(key) {
    let item = this.collection.getItem(key);

    if (!item) {
      return;
    } // If focus was on a column, then focus the next column


    if (item.type === 'column') {
      return this.direction === 'rtl' ? this.findPreviousColumnKey(item) : this.findNextColumnKey(item);
    }

    return super.getKeyRightOf(key);
  }

  getKeyLeftOf(key) {
    let item = this.collection.getItem(key);

    if (!item) {
      return;
    } // If focus was on a column, then focus the previous column


    if (item.type === 'column') {
      return this.direction === 'rtl' ? this.findNextColumnKey(item) : this.findPreviousColumnKey(item);
    }

    return super.getKeyLeftOf(key);
  }

  getKeyForSearch(search, fromKey) {
    if (!this.collator) {
      return null;
    }

    let collection = this.collection;
    let key = fromKey != null ? fromKey : this.getFirstKey(); // If the starting key is a cell, search from its parent row.

    let startItem = collection.getItem(key);

    if (startItem.type === 'cell') {
      key = startItem.parentKey;
    }

    let hasWrapped = false;

    while (key != null) {
      let item = collection.getItem(key); // Check each of the row header cells in this row for a match

      for (let cell of item.childNodes) {
        let column = collection.columns[cell.index];

        if (collection.rowHeaderColumnKeys.has(column.key) && cell.textValue) {
          let substring = cell.textValue.slice(0, search.length);

          if (this.collator.compare(substring, search) === 0) {
            // If we started on a cell, end on the matching cell. Otherwise, end on the row.
            let fromItem = fromKey != null ? collection.getItem(fromKey) : startItem;
            return fromItem.type === 'cell' ? cell.key : item.key;
          }
        }
      }

      key = this.getKeyBelow(key); // Wrap around when reaching the end of the collection

      if (key == null && !hasWrapped) {
        key = this.getFirstKey();
        hasWrapped = true;
      }
    }

    return null;
  }

}

// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/ar-AE.json
var $c78a5f3d053cfe919804d48f6$exports = {};
$c78a5f3d053cfe919804d48f6$exports = JSON.parse("{\"ascending\":\"تصاعدي\",\"ascendingSort\":\"ترتيب حسب العمود {columnName} بترتيب تصاعدي\",\"descending\":\"تنازلي\",\"descendingSort\":\"ترتيب حسب العمود {columnName} بترتيب تنازلي\",\"select\":\"تحديد\",\"selectAll\":\"تحديد الكل\",\"sortable\":\"عمود قابل للترتيب\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/bg-BG.json
var $b4e0c158b3f3fe8de3f5bae6eb9a0c3$exports = {};
$b4e0c158b3f3fe8de3f5bae6eb9a0c3$exports = JSON.parse("{\"ascending\":\"възходящ\",\"ascendingSort\":\"сортирано по колона {columnName} във възходящ ред\",\"descending\":\"низходящ\",\"descendingSort\":\"сортирано по колона {columnName} в низходящ ред\",\"select\":\"Изберете\",\"selectAll\":\"Изберете всичко\",\"sortable\":\"сортираща колона\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/cs-CZ.json
var $f651dd03e7dee77b6b81b4aa1c002d$exports = {};
$f651dd03e7dee77b6b81b4aa1c002d$exports = JSON.parse("{\"ascending\":\"vzestupně\",\"ascendingSort\":\"řazeno vzestupně podle sloupce {columnName}\",\"descending\":\"sestupně\",\"descendingSort\":\"řazeno sestupně podle sloupce {columnName}\",\"select\":\"Vybrat\",\"selectAll\":\"Vybrat vše\",\"sortable\":\"sloupec s možností řazení\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/da-DK.json
var $eb6fd05bd5c9430cd5fc65c40e67c3$exports = {};
$eb6fd05bd5c9430cd5fc65c40e67c3$exports = JSON.parse("{\"ascending\":\"stigende\",\"ascendingSort\":\"sorteret efter kolonne {columnName} i stigende rækkefølge\",\"descending\":\"faldende\",\"descendingSort\":\"sorteret efter kolonne {columnName} i faldende rækkefølge\",\"select\":\"Vælg\",\"selectAll\":\"Vælg alle\",\"sortable\":\"sorterbar kolonne\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/de-DE.json
var $afb7b9ae6d6e970aa4d5a2eee2f5ed73$exports = {};
$afb7b9ae6d6e970aa4d5a2eee2f5ed73$exports = JSON.parse("{\"ascending\":\"aufsteigend\",\"ascendingSort\":\"sortiert nach Spalte {columnName} in aufsteigender Reihenfolge\",\"descending\":\"absteigend\",\"descendingSort\":\"sortiert nach Spalte {columnName} in absteigender Reihenfolge\",\"select\":\"Auswählen\",\"selectAll\":\"Alles auswählen\",\"sortable\":\"sortierbare Spalte\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/el-GR.json
var $d29004535be97c761ac3c0fd8f5$exports = {};
$d29004535be97c761ac3c0fd8f5$exports = JSON.parse("{\"ascending\":\"αύξουσα\",\"ascendingSort\":\"διαλογή ανά στήλη {columnName} σε αύξουσα σειρά\",\"descending\":\"φθίνουσα\",\"descendingSort\":\"διαλογή ανά στήλη {columnName} σε φθίνουσα σειρά\",\"select\":\"Επιλογή\",\"selectAll\":\"Επιλογή όλων\",\"sortable\":\"Στήλη διαλογής\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/en-US.json
var $a5fdb79c8812ee1e75a521108ef37d77$exports = {};
$a5fdb79c8812ee1e75a521108ef37d77$exports = JSON.parse("{\"select\":\"Select\",\"selectAll\":\"Select All\",\"sortable\":\"sortable column\",\"ascending\":\"ascending\",\"descending\":\"descending\",\"ascendingSort\":\"sorted by column {columnName} in ascending order\",\"descendingSort\":\"sorted by column {columnName} in descending order\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/es-ES.json
var $dfe8ebd8f763328f528c0ad3c7562bdf$exports = {};
$dfe8ebd8f763328f528c0ad3c7562bdf$exports = JSON.parse("{\"ascending\":\"de subida\",\"ascendingSort\":\"ordenado por columna {columnName} en orden de subida\",\"descending\":\"de bajada\",\"descendingSort\":\"ordenado por columna {columnName} en orden de bajada\",\"select\":\"Seleccionar\",\"selectAll\":\"Seleccionar todos\",\"sortable\":\"columna ordenable\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/et-EE.json
var $d23a2453a1f8a15deca01ea436db1123$exports = {};
$d23a2453a1f8a15deca01ea436db1123$exports = JSON.parse("{\"ascending\":\"tõusev järjestus\",\"ascendingSort\":\"sorditud veeru järgi {columnName} tõusvas järjestuses\",\"descending\":\"laskuv järjestus\",\"descendingSort\":\"sorditud veeru järgi {columnName} laskuvas järjestuses\",\"select\":\"Vali\",\"selectAll\":\"Vali kõik\",\"sortable\":\"sorditav veerg\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/fi-FI.json
var $fb2cc180fa5152799dde37135758203a$exports = {};
$fb2cc180fa5152799dde37135758203a$exports = JSON.parse("{\"ascending\":\"nouseva\",\"ascendingSort\":\"lajiteltu sarakkeen {columnName} mukaan nousevassa järjestyksessä\",\"descending\":\"laskeva\",\"descendingSort\":\"lajiteltu sarakkeen {columnName} mukaan laskevassa järjestyksessä\",\"select\":\"Valitse\",\"selectAll\":\"Valitse kaikki\",\"sortable\":\"lajiteltava sarake\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/fr-FR.json
var $f74cdf38efce0e02fdc2a54ca8e2d$exports = {};
$f74cdf38efce0e02fdc2a54ca8e2d$exports = JSON.parse("{\"ascending\":\"croissant\",\"ascendingSort\":\"trié en fonction de la colonne {columnName} par ordre croissant\",\"descending\":\"décroissant\",\"descendingSort\":\"trié en fonction de la colonne {columnName} par ordre décroissant\",\"select\":\"Sélectionner\",\"selectAll\":\"Sélectionner tout\",\"sortable\":\"colonne triable\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/he-IL.json
var $dee24156021523ebcfd4a2559bb8a71$exports = {};
$dee24156021523ebcfd4a2559bb8a71$exports = JSON.parse("{\"ascending\":\"עולה\",\"ascendingSort\":\"מוין לפי עמודה {columnName} בסדר עולה\",\"descending\":\"יורד\",\"descendingSort\":\"מוין לפי עמודה {columnName} בסדר יורד\",\"select\":\"בחר\",\"selectAll\":\"בחר הכול\",\"sortable\":\"עמודה שניתן למיין\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/hr-HR.json
var $f53c252ee23d704c4bb1f1f1175$exports = {};
$f53c252ee23d704c4bb1f1f1175$exports = JSON.parse("{\"ascending\":\"rastući\",\"ascendingSort\":\"razvrstano po stupcima {columnName} rastućem redoslijedom\",\"descending\":\"padajući\",\"descendingSort\":\"razvrstano po stupcima {columnName} padajućim redoslijedom\",\"select\":\"Odaberite\",\"selectAll\":\"Odaberite sve\",\"sortable\":\"stupac koji se može razvrstati\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/hu-HU.json
var $d76f3cb4fc517ef8967e26de1dd235f9$exports = {};
$d76f3cb4fc517ef8967e26de1dd235f9$exports = JSON.parse("{\"ascending\":\"növekvő\",\"ascendingSort\":\"rendezve a(z) {columnName} oszlop szerint, növekvő sorrendben\",\"descending\":\"csökkenő\",\"descendingSort\":\"rendezve a(z) {columnName} oszlop szerint, csökkenő sorrendben\",\"select\":\"Kijelölés\",\"selectAll\":\"Összes kijelölése\",\"sortable\":\"rendezendő oszlop\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/it-IT.json
var $a5f5466db856bde9a318d316a7b9a$exports = {};
$a5f5466db856bde9a318d316a7b9a$exports = JSON.parse("{\"ascending\":\"crescente\",\"ascendingSort\":\"in ordine crescente in base alla colonna {columnName}\",\"descending\":\"decrescente\",\"descendingSort\":\"in ordine decrescente in base alla colonna {columnName}\",\"select\":\"Seleziona\",\"selectAll\":\"Seleziona tutto\",\"sortable\":\"colonna ordinabile\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/ja-JP.json
var $a72f8d03d4866b0b0942fc7364f6c17f$exports = {};
$a72f8d03d4866b0b0942fc7364f6c17f$exports = JSON.parse("{\"ascending\":\"昇順\",\"ascendingSort\":\"列 {columnName} を昇順で並べ替え\",\"descending\":\"降順\",\"descendingSort\":\"列 {columnName} を降順で並べ替え\",\"select\":\"選択\",\"selectAll\":\"すべて選択\",\"sortable\":\"並べ替え可能な列\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/ko-KR.json
var $afa7d8f59ebcb29113d9b76f75d93$exports = {};
$afa7d8f59ebcb29113d9b76f75d93$exports = JSON.parse("{\"ascending\":\"오름차순\",\"ascendingSort\":\"{columnName} 열을 기준으로 오름차순으로 정렬됨\",\"descending\":\"내림차순\",\"descendingSort\":\"{columnName} 열을 기준으로 내림차순으로 정렬됨\",\"select\":\"선택\",\"selectAll\":\"모두 선택\",\"sortable\":\"정렬 가능한 열\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/lt-LT.json
var $beac8373bccb98df7426400cbb42$exports = {};
$beac8373bccb98df7426400cbb42$exports = JSON.parse("{\"ascending\":\"didėjančia tvarka\",\"ascendingSort\":\"surikiuota pagal stulpelį {columnName} didėjančia tvarka\",\"descending\":\"mažėjančia tvarka\",\"descendingSort\":\"surikiuota pagal stulpelį {columnName} mažėjančia tvarka\",\"select\":\"Pasirinkti\",\"selectAll\":\"Pasirinkti viską\",\"sortable\":\"rikiuojamas stulpelis\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/lv-LV.json
var $c39fc9f76532e2d96005ff819ea6fb48$exports = {};
$c39fc9f76532e2d96005ff819ea6fb48$exports = JSON.parse("{\"ascending\":\"augošā secībā\",\"ascendingSort\":\"kārtots pēc kolonnas {columnName} augošā secībā\",\"descending\":\"dilstošā secībā\",\"descendingSort\":\"kārtots pēc kolonnas {columnName} dilstošā secībā\",\"select\":\"Atlasīt\",\"selectAll\":\"Atlasīt visu\",\"sortable\":\"kārtojamā kolonna\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/nb-NO.json
var $bd42e2aab422cdeb54266963268ab53$exports = {};
$bd42e2aab422cdeb54266963268ab53$exports = JSON.parse("{\"ascending\":\"stigende\",\"ascendingSort\":\"sortert etter kolonne {columnName} i stigende rekkefølge\",\"descending\":\"synkende\",\"descendingSort\":\"sortert etter kolonne {columnName} i synkende rekkefølge\",\"select\":\"Velg\",\"selectAll\":\"Velg alle\",\"sortable\":\"kolonne som kan sorteres\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/nl-NL.json
var $a755f8465ba08d876a6edc702f660c$exports = {};
$a755f8465ba08d876a6edc702f660c$exports = JSON.parse("{\"ascending\":\"oplopend\",\"ascendingSort\":\"gesorteerd in oplopende volgorde in kolom {columnName}\",\"descending\":\"aflopend\",\"descendingSort\":\"gesorteerd in aflopende volgorde in kolom {columnName}\",\"select\":\"Selecteren\",\"selectAll\":\"Alles selecteren\",\"sortable\":\"sorteerbare kolom\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/pl-PL.json
var $fb7b2809312b948f135fc12bee25695$exports = {};
$fb7b2809312b948f135fc12bee25695$exports = JSON.parse("{\"ascending\":\"rosnąco\",\"ascendingSort\":\"posortowano według kolumny {columnName} w porządku rosnącym\",\"descending\":\"malejąco\",\"descendingSort\":\"posortowano według kolumny {columnName} w porządku malejącym\",\"select\":\"Zaznacz\",\"selectAll\":\"Zaznacz wszystko\",\"sortable\":\"kolumna z możliwością sortowania\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/pt-BR.json
var $e0a153f32c1a45327d181dd0c500676$exports = {};
$e0a153f32c1a45327d181dd0c500676$exports = JSON.parse("{\"ascending\":\"crescente\",\"ascendingSort\":\"classificado pela coluna {columnName} em ordem crescente\",\"descending\":\"decrescente\",\"descendingSort\":\"classificado pela coluna {columnName} em ordem decrescente\",\"select\":\"Selecionar\",\"selectAll\":\"Selecionar tudo\",\"sortable\":\"coluna classificável\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/pt-PT.json
var $ad3816a8b2225b00c10542dd14ac37d3$exports = {};
$ad3816a8b2225b00c10542dd14ac37d3$exports = JSON.parse("{\"ascending\":\"ascendente\",\"ascendingSort\":\"Ordenar por coluna {columnName} em ordem ascendente\",\"descending\":\"descendente\",\"descendingSort\":\"Ordenar por coluna {columnName} em ordem descendente\",\"select\":\"Selecionar\",\"selectAll\":\"Selecionar tudo\",\"sortable\":\"Coluna ordenável\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/ro-RO.json
var $f0b80e4973d410b36a72479b0fc90$exports = {};
$f0b80e4973d410b36a72479b0fc90$exports = JSON.parse("{\"ascending\":\"crescătoare\",\"ascendingSort\":\"sortate după coloana {columnName} în ordine crescătoare\",\"descending\":\"descrescătoare\",\"descendingSort\":\"sortate după coloana {columnName} în ordine descrescătoare\",\"select\":\"Selectare\",\"selectAll\":\"Selectare totală\",\"sortable\":\"coloană sortabilă\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/ru-RU.json
var $cfda2747a3f6051c45ad9108a9a705$exports = {};
$cfda2747a3f6051c45ad9108a9a705$exports = JSON.parse("{\"ascending\":\"возрастание\",\"ascendingSort\":\"сортировать столбец {columnName} в порядке возрастания\",\"descending\":\"убывание\",\"descendingSort\":\"сортировать столбец {columnName} в порядке убывания\",\"select\":\"Выбрать\",\"selectAll\":\"Выбрать все\",\"sortable\":\"сортируемый столбец\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/sk-SK.json
var $d339184d3b85ce4512ac28ce36ed4c$exports = {};
$d339184d3b85ce4512ac28ce36ed4c$exports = JSON.parse("{\"ascending\":\"vzostupne\",\"ascendingSort\":\"zoradené zostupne podľa stĺpca {columnName}\",\"descending\":\"zostupne\",\"descendingSort\":\"zoradené zostupne podľa stĺpca {columnName}\",\"select\":\"Vybrať\",\"selectAll\":\"Vybrať všetko\",\"sortable\":\"zoraditeľný stĺpec\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/sl-SI.json
var $c26edd75631a1047e596d7f3a08ac91$exports = {};
$c26edd75631a1047e596d7f3a08ac91$exports = JSON.parse("{\"ascending\":\"naraščajoče\",\"ascendingSort\":\"razvrščeno po stolpcu {columnName} v naraščajočem vrstnem redu\",\"descending\":\"padajoče\",\"descendingSort\":\"razvrščeno po stolpcu {columnName} v padajočem vrstnem redu\",\"select\":\"Izberite\",\"selectAll\":\"Izberite vse\",\"sortable\":\"razvrstljivi stolpec\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/sr-SP.json
var $ddc13676954a8ad8e15fb129afdfc207$exports = {};
$ddc13676954a8ad8e15fb129afdfc207$exports = JSON.parse("{\"ascending\":\"rastući\",\"ascendingSort\":\"sortirano po kolonama {columnName} rastućim redosledom\",\"descending\":\"padajući\",\"descendingSort\":\"sortirano po kolonama {columnName} padajućim redosledom\",\"select\":\"Izaberite\",\"selectAll\":\"Izaberite sve\",\"sortable\":\"kolona koja se može sortirati\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/sv-SE.json
var $f3e3a34a322ae9d9a02dc1fd1993bbe$exports = {};
$f3e3a34a322ae9d9a02dc1fd1993bbe$exports = JSON.parse("{\"ascending\":\"stigande\",\"ascendingSort\":\"sorterat på kolumn {columnName} i stigande ordning\",\"descending\":\"fallande\",\"descendingSort\":\"sorterat på kolumn {columnName} i fallande ordning\",\"select\":\"Markera\",\"selectAll\":\"Markera allt\",\"sortable\":\"sorterbar kolumn\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/tr-TR.json
var $e43dd3d8f81c5a7432b792e1f6f98055$exports = {};
$e43dd3d8f81c5a7432b792e1f6f98055$exports = JSON.parse("{\"ascending\":\"artan sırada\",\"ascendingSort\":\"{columnName} sütuna göre artan düzende sırala\",\"descending\":\"azalan sırada\",\"descendingSort\":\"{columnName} sütuna göre azalan düzende sırala\",\"select\":\"Seç\",\"selectAll\":\"Tümünü Seç\",\"sortable\":\"Sıralanabilir sütun\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/uk-UA.json
var $c3b936950f1f3334d7b4d4f757af68e$exports = {};
$c3b936950f1f3334d7b4d4f757af68e$exports = JSON.parse("{\"ascending\":\"висхідний\",\"ascendingSort\":\"відсортовано за стовпцем {columnName} у висхідному порядку\",\"descending\":\"низхідний\",\"descendingSort\":\"відсортовано за стовпцем {columnName} у низхідному порядку\",\"select\":\"Вибрати\",\"selectAll\":\"Вибрати все\",\"sortable\":\"сортувальний стовпець\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/zh-CN.json
var $d58d47f7a6e3477a53b4bae760321$exports = {};
$d58d47f7a6e3477a53b4bae760321$exports = JSON.parse("{\"ascending\":\"升序\",\"ascendingSort\":\"按列 {columnName} 升序排序\",\"descending\":\"降序\",\"descendingSort\":\"按列 {columnName} 降序排序\",\"select\":\"选择\",\"selectAll\":\"全选\",\"sortable\":\"可排序的列\"}");
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/table/intl/zh-TW.json
var $bcd230dc1cabfc61b9e27d841f17b04$exports = {};
$bcd230dc1cabfc61b9e27d841f17b04$exports = JSON.parse("{\"ascending\":\"遞增\",\"ascendingSort\":\"已依據「{columnName}」欄遞增排序\",\"descending\":\"遞減\",\"descendingSort\":\"已依據「{columnName}」欄遞減排序\",\"select\":\"選取\",\"selectAll\":\"全選\",\"sortable\":\"可排序的欄\"}");
// @ts-ignore
const $ef7f9bcc41f88bc493d7fa467d0446$var$intlMessages = {
  "ar-AE": _babelRuntimeHelpersInteropRequireDefault($c78a5f3d053cfe919804d48f6$exports).default,
  "bg-BG": _babelRuntimeHelpersInteropRequireDefault($b4e0c158b3f3fe8de3f5bae6eb9a0c3$exports).default,
  "cs-CZ": _babelRuntimeHelpersInteropRequireDefault($f651dd03e7dee77b6b81b4aa1c002d$exports).default,
  "da-DK": _babelRuntimeHelpersInteropRequireDefault($eb6fd05bd5c9430cd5fc65c40e67c3$exports).default,
  "de-DE": _babelRuntimeHelpersInteropRequireDefault($afb7b9ae6d6e970aa4d5a2eee2f5ed73$exports).default,
  "el-GR": _babelRuntimeHelpersInteropRequireDefault($d29004535be97c761ac3c0fd8f5$exports).default,
  "en-US": _babelRuntimeHelpersInteropRequireDefault($a5fdb79c8812ee1e75a521108ef37d77$exports).default,
  "es-ES": _babelRuntimeHelpersInteropRequireDefault($dfe8ebd8f763328f528c0ad3c7562bdf$exports).default,
  "et-EE": _babelRuntimeHelpersInteropRequireDefault($d23a2453a1f8a15deca01ea436db1123$exports).default,
  "fi-FI": _babelRuntimeHelpersInteropRequireDefault($fb2cc180fa5152799dde37135758203a$exports).default,
  "fr-FR": _babelRuntimeHelpersInteropRequireDefault($f74cdf38efce0e02fdc2a54ca8e2d$exports).default,
  "he-IL": _babelRuntimeHelpersInteropRequireDefault($dee24156021523ebcfd4a2559bb8a71$exports).default,
  "hr-HR": _babelRuntimeHelpersInteropRequireDefault($f53c252ee23d704c4bb1f1f1175$exports).default,
  "hu-HU": _babelRuntimeHelpersInteropRequireDefault($d76f3cb4fc517ef8967e26de1dd235f9$exports).default,
  "it-IT": _babelRuntimeHelpersInteropRequireDefault($a5f5466db856bde9a318d316a7b9a$exports).default,
  "ja-JP": _babelRuntimeHelpersInteropRequireDefault($a72f8d03d4866b0b0942fc7364f6c17f$exports).default,
  "ko-KR": _babelRuntimeHelpersInteropRequireDefault($afa7d8f59ebcb29113d9b76f75d93$exports).default,
  "lt-LT": _babelRuntimeHelpersInteropRequireDefault($beac8373bccb98df7426400cbb42$exports).default,
  "lv-LV": _babelRuntimeHelpersInteropRequireDefault($c39fc9f76532e2d96005ff819ea6fb48$exports).default,
  "nb-NO": _babelRuntimeHelpersInteropRequireDefault($bd42e2aab422cdeb54266963268ab53$exports).default,
  "nl-NL": _babelRuntimeHelpersInteropRequireDefault($a755f8465ba08d876a6edc702f660c$exports).default,
  "pl-PL": _babelRuntimeHelpersInteropRequireDefault($fb7b2809312b948f135fc12bee25695$exports).default,
  "pt-BR": _babelRuntimeHelpersInteropRequireDefault($e0a153f32c1a45327d181dd0c500676$exports).default,
  "pt-PT": _babelRuntimeHelpersInteropRequireDefault($ad3816a8b2225b00c10542dd14ac37d3$exports).default,
  "ro-RO": _babelRuntimeHelpersInteropRequireDefault($f0b80e4973d410b36a72479b0fc90$exports).default,
  "ru-RU": _babelRuntimeHelpersInteropRequireDefault($cfda2747a3f6051c45ad9108a9a705$exports).default,
  "sk-SK": _babelRuntimeHelpersInteropRequireDefault($d339184d3b85ce4512ac28ce36ed4c$exports).default,
  "sl-SI": _babelRuntimeHelpersInteropRequireDefault($c26edd75631a1047e596d7f3a08ac91$exports).default,
  "sr-SP": _babelRuntimeHelpersInteropRequireDefault($ddc13676954a8ad8e15fb129afdfc207$exports).default,
  "sv-SE": _babelRuntimeHelpersInteropRequireDefault($f3e3a34a322ae9d9a02dc1fd1993bbe$exports).default,
  "tr-TR": _babelRuntimeHelpersInteropRequireDefault($e43dd3d8f81c5a7432b792e1f6f98055$exports).default,
  "uk-UA": _babelRuntimeHelpersInteropRequireDefault($c3b936950f1f3334d7b4d4f757af68e$exports).default,
  "zh-CN": _babelRuntimeHelpersInteropRequireDefault($d58d47f7a6e3477a53b4bae760321$exports).default,
  "zh-TW": _babelRuntimeHelpersInteropRequireDefault($bcd230dc1cabfc61b9e27d841f17b04$exports).default
};

/**
 * Provides the behavior and accessibility implementation for a table component.
 * A table displays data in rows and columns and enables a user to navigate its contents via directional navigation keys,
 * and optionally supports row selection and sorting.
 * @param props - Props for the table.
 * @param state - State for the table, as returned by `useTableState`.
 * @param ref - The ref attached to the table element.
 */
function useTable(props, state, ref) {
  let {
    keyboardDelegate,
    isVirtualized,
    layout
  } = props; // By default, a KeyboardDelegate is provided which uses the DOM to query layout information (e.g. for page up/page down).
  // When virtualized, the layout object will be passed in as a prop and override this.

  let collator = useCollator({
    usage: 'search',
    sensitivity: 'base'
  });
  let {
    direction
  } = useLocale();
  let delegate = useMemo(() => keyboardDelegate || new $d19e2fa944f991da21b1694810eb968d$export$TableKeyboardDelegate({
    collection: state.collection,
    disabledKeys: state.disabledKeys,
    ref,
    direction,
    collator,
    layout
  }), [keyboardDelegate, state.collection, state.disabledKeys, ref, direction, collator, layout]);
  let id = useId();
  $b62c6c69dd91ba8b8df7cfb5ba576$export$gridIds.set(state, id);
  let {
    gridProps
  } = useGrid(_babelRuntimeHelpersExtends({}, props, {
    id,
    keyboardDelegate: delegate,

    getRowText(key) {
      let added = state.collection.getItem(key); // If the row has a textValue, use that.

      if (added.textValue != null) {
        return added.textValue;
      } // Otherwise combine the text of each of the row header columns.


      let rowHeaderColumnKeys = state.collection.rowHeaderColumnKeys;

      if (rowHeaderColumnKeys) {
        let text = [];

        for (let cell of added.childNodes) {
          let column = state.collection.columns[cell.index];

          if (rowHeaderColumnKeys.has(column.key) && cell.textValue) {
            text.push(cell.textValue);
          }

          if (text.length === rowHeaderColumnKeys.size) {
            break;
          }
        }

        return text.join(' ');
      }

      return '';
    }

  }), state, ref); // Override to include header rows

  if (isVirtualized) {
    gridProps['aria-rowcount'] = state.collection.size + state.collection.headerRows.length;
  }

  let {
    column,
    direction: sortDirection
  } = state.sortDescriptor || {};
  let formatMessage = useMessageFormatter($ef7f9bcc41f88bc493d7fa467d0446$var$intlMessages);
  let sortDescription = useMemo(() => {
    var _state$collection$col;

    let columnName = (_state$collection$col = state.collection.columns.find(c => c.key === column)) == null ? void 0 : _state$collection$col.textValue;
    return sortDirection && column ? formatMessage(sortDirection + "Sort", {
      columnName
    }) : undefined;
  }, [sortDirection, column, state.collection.columns]);
  let descriptionProps = useDescription(sortDescription); // Only announce after initial render, tabbing to the table will tell you the initial sort info already

  useUpdateEffect(() => {
    announce(sortDescription, 'assertive', 500);
  }, [sortDescription]);
  return {
    gridProps: mergeProps(gridProps, descriptionProps)
  };
}

exports.useTable = useTable;
// @ts-ignore
const $f8dbf447e53eb309a7cd794e$var$intlMessages = {
  "ar-AE": _babelRuntimeHelpersInteropRequireDefault($c78a5f3d053cfe919804d48f6$exports).default,
  "bg-BG": _babelRuntimeHelpersInteropRequireDefault($b4e0c158b3f3fe8de3f5bae6eb9a0c3$exports).default,
  "cs-CZ": _babelRuntimeHelpersInteropRequireDefault($f651dd03e7dee77b6b81b4aa1c002d$exports).default,
  "da-DK": _babelRuntimeHelpersInteropRequireDefault($eb6fd05bd5c9430cd5fc65c40e67c3$exports).default,
  "de-DE": _babelRuntimeHelpersInteropRequireDefault($afb7b9ae6d6e970aa4d5a2eee2f5ed73$exports).default,
  "el-GR": _babelRuntimeHelpersInteropRequireDefault($d29004535be97c761ac3c0fd8f5$exports).default,
  "en-US": _babelRuntimeHelpersInteropRequireDefault($a5fdb79c8812ee1e75a521108ef37d77$exports).default,
  "es-ES": _babelRuntimeHelpersInteropRequireDefault($dfe8ebd8f763328f528c0ad3c7562bdf$exports).default,
  "et-EE": _babelRuntimeHelpersInteropRequireDefault($d23a2453a1f8a15deca01ea436db1123$exports).default,
  "fi-FI": _babelRuntimeHelpersInteropRequireDefault($fb2cc180fa5152799dde37135758203a$exports).default,
  "fr-FR": _babelRuntimeHelpersInteropRequireDefault($f74cdf38efce0e02fdc2a54ca8e2d$exports).default,
  "he-IL": _babelRuntimeHelpersInteropRequireDefault($dee24156021523ebcfd4a2559bb8a71$exports).default,
  "hr-HR": _babelRuntimeHelpersInteropRequireDefault($f53c252ee23d704c4bb1f1f1175$exports).default,
  "hu-HU": _babelRuntimeHelpersInteropRequireDefault($d76f3cb4fc517ef8967e26de1dd235f9$exports).default,
  "it-IT": _babelRuntimeHelpersInteropRequireDefault($a5f5466db856bde9a318d316a7b9a$exports).default,
  "ja-JP": _babelRuntimeHelpersInteropRequireDefault($a72f8d03d4866b0b0942fc7364f6c17f$exports).default,
  "ko-KR": _babelRuntimeHelpersInteropRequireDefault($afa7d8f59ebcb29113d9b76f75d93$exports).default,
  "lt-LT": _babelRuntimeHelpersInteropRequireDefault($beac8373bccb98df7426400cbb42$exports).default,
  "lv-LV": _babelRuntimeHelpersInteropRequireDefault($c39fc9f76532e2d96005ff819ea6fb48$exports).default,
  "nb-NO": _babelRuntimeHelpersInteropRequireDefault($bd42e2aab422cdeb54266963268ab53$exports).default,
  "nl-NL": _babelRuntimeHelpersInteropRequireDefault($a755f8465ba08d876a6edc702f660c$exports).default,
  "pl-PL": _babelRuntimeHelpersInteropRequireDefault($fb7b2809312b948f135fc12bee25695$exports).default,
  "pt-BR": _babelRuntimeHelpersInteropRequireDefault($e0a153f32c1a45327d181dd0c500676$exports).default,
  "pt-PT": _babelRuntimeHelpersInteropRequireDefault($ad3816a8b2225b00c10542dd14ac37d3$exports).default,
  "ro-RO": _babelRuntimeHelpersInteropRequireDefault($f0b80e4973d410b36a72479b0fc90$exports).default,
  "ru-RU": _babelRuntimeHelpersInteropRequireDefault($cfda2747a3f6051c45ad9108a9a705$exports).default,
  "sk-SK": _babelRuntimeHelpersInteropRequireDefault($d339184d3b85ce4512ac28ce36ed4c$exports).default,
  "sl-SI": _babelRuntimeHelpersInteropRequireDefault($c26edd75631a1047e596d7f3a08ac91$exports).default,
  "sr-SP": _babelRuntimeHelpersInteropRequireDefault($ddc13676954a8ad8e15fb129afdfc207$exports).default,
  "sv-SE": _babelRuntimeHelpersInteropRequireDefault($f3e3a34a322ae9d9a02dc1fd1993bbe$exports).default,
  "tr-TR": _babelRuntimeHelpersInteropRequireDefault($e43dd3d8f81c5a7432b792e1f6f98055$exports).default,
  "uk-UA": _babelRuntimeHelpersInteropRequireDefault($c3b936950f1f3334d7b4d4f757af68e$exports).default,
  "zh-CN": _babelRuntimeHelpersInteropRequireDefault($d58d47f7a6e3477a53b4bae760321$exports).default,
  "zh-TW": _babelRuntimeHelpersInteropRequireDefault($bcd230dc1cabfc61b9e27d841f17b04$exports).default
};

/**
 * Provides the behavior and accessibility implementation for a column header in a table.
 * @param props - Props for the column header.
 * @param state - State of the table, as returned by `useTableState`.
 * @param ref - The ref attached to the column header element.
 */
function useTableColumnHeader(props, state, ref) {
  var _state$sortDescriptor, _state$sortDescriptor2;

  let {
    node
  } = props;
  let allowsSorting = node.props.allowsSorting;
  let {
    gridCellProps
  } = useGridCell(props, state, ref);
  let isSelectionCellDisabled = node.props.isSelectionCell && state.selectionManager.selectionMode === 'single';
  let {
    pressProps
  } = usePress({
    isDisabled: !allowsSorting || isSelectionCellDisabled,

    onPress() {
      state.sort(node.key);
    }

  }); // Needed to pick up the focusable context, enabling things like Tooltips for example

  let {
    focusableProps
  } = useFocusable({}, ref);
  let ariaSort = null;
  let isSortedColumn = ((_state$sortDescriptor = state.sortDescriptor) == null ? void 0 : _state$sortDescriptor.column) === node.key;
  let sortDirection = (_state$sortDescriptor2 = state.sortDescriptor) == null ? void 0 : _state$sortDescriptor2.direction; // aria-sort not supported in Android Talkback

  if (node.props.allowsSorting && !isAndroid()) {
    ariaSort = isSortedColumn ? sortDirection : 'none';
  }

  let formatMessage = useMessageFormatter($f8dbf447e53eb309a7cd794e$var$intlMessages);
  let sortDescription;

  if (allowsSorting) {
    sortDescription = "" + formatMessage('sortable'); // Android Talkback doesn't support aria-sort so we add sort order details to the aria-described by here

    if (isSortedColumn && sortDirection && isAndroid()) {
      sortDescription = sortDescription + ", " + formatMessage(sortDirection);
    }
  }

  let descriptionProps = useDescription(sortDescription);
  return {
    columnHeaderProps: _babelRuntimeHelpersExtends({}, mergeProps(gridCellProps, pressProps, focusableProps, descriptionProps), {
      role: 'columnheader',
      id: $b62c6c69dd91ba8b8df7cfb5ba576$export$getColumnHeaderId(state, node.key),
      'aria-colspan': node.colspan && node.colspan > 1 ? node.colspan : null,
      'aria-sort': ariaSort
    })
  };
}

exports.useTableColumnHeader = useTableColumnHeader;

/**
 * Provides the behavior and accessibility implementation for a row in a table.
 * @param props - Props for the row.
 * @param state - State of the table, as returned by `useTableState`.
 */
function useTableRow(props, state, ref) {
  let {
    node
  } = props;
  let {
    rowProps
  } = useGridRow(props, state, ref);
  return {
    rowProps: _babelRuntimeHelpersExtends({}, rowProps, {
      'aria-labelledby': $b62c6c69dd91ba8b8df7cfb5ba576$export$getRowLabelledBy(state, node.key)
    })
  };
}

exports.useTableRow = useTableRow;

/**
 * Provides the behavior and accessibility implementation for a header row in a table.
 * @param props - Props for the row.
 * @param state - State of the table, as returned by `useTableState`.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function useTableHeaderRow(props, state, ref) {
  let {
    node,
    isVirtualized
  } = props;
  let rowProps = {
    role: 'row'
  };

  if (isVirtualized) {
    rowProps['aria-rowindex'] = node.index + 1; // aria-rowindex is 1 based
  }

  return {
    rowProps
  };
}

exports.useTableHeaderRow = useTableHeaderRow;

/**
 * Provides the behavior and accessibility implementation for a cell in a table.
 * @param props - Props for the cell.
 * @param state - State of the table, as returned by `useTableState`.
 * @param ref - The ref attached to the cell element.
 */
function useTableCell(props, state, ref) {
  let {
    gridCellProps
  } = useGridCell(props, state, ref);
  let columnKey = props.node.column.key;

  if (state.collection.rowHeaderColumnKeys.has(columnKey)) {
    gridCellProps.role = 'rowheader';
    gridCellProps.id = $b62c6c69dd91ba8b8df7cfb5ba576$export$getCellId(state, props.node.parentKey, columnKey);
  }

  return {
    gridCellProps
  };
}

exports.useTableCell = useTableCell;
// @ts-ignore
const $c82313c140cce815a7e91604cb62d626$var$intlMessages = {
  "ar-AE": _babelRuntimeHelpersInteropRequireDefault($c78a5f3d053cfe919804d48f6$exports).default,
  "bg-BG": _babelRuntimeHelpersInteropRequireDefault($b4e0c158b3f3fe8de3f5bae6eb9a0c3$exports).default,
  "cs-CZ": _babelRuntimeHelpersInteropRequireDefault($f651dd03e7dee77b6b81b4aa1c002d$exports).default,
  "da-DK": _babelRuntimeHelpersInteropRequireDefault($eb6fd05bd5c9430cd5fc65c40e67c3$exports).default,
  "de-DE": _babelRuntimeHelpersInteropRequireDefault($afb7b9ae6d6e970aa4d5a2eee2f5ed73$exports).default,
  "el-GR": _babelRuntimeHelpersInteropRequireDefault($d29004535be97c761ac3c0fd8f5$exports).default,
  "en-US": _babelRuntimeHelpersInteropRequireDefault($a5fdb79c8812ee1e75a521108ef37d77$exports).default,
  "es-ES": _babelRuntimeHelpersInteropRequireDefault($dfe8ebd8f763328f528c0ad3c7562bdf$exports).default,
  "et-EE": _babelRuntimeHelpersInteropRequireDefault($d23a2453a1f8a15deca01ea436db1123$exports).default,
  "fi-FI": _babelRuntimeHelpersInteropRequireDefault($fb2cc180fa5152799dde37135758203a$exports).default,
  "fr-FR": _babelRuntimeHelpersInteropRequireDefault($f74cdf38efce0e02fdc2a54ca8e2d$exports).default,
  "he-IL": _babelRuntimeHelpersInteropRequireDefault($dee24156021523ebcfd4a2559bb8a71$exports).default,
  "hr-HR": _babelRuntimeHelpersInteropRequireDefault($f53c252ee23d704c4bb1f1f1175$exports).default,
  "hu-HU": _babelRuntimeHelpersInteropRequireDefault($d76f3cb4fc517ef8967e26de1dd235f9$exports).default,
  "it-IT": _babelRuntimeHelpersInteropRequireDefault($a5f5466db856bde9a318d316a7b9a$exports).default,
  "ja-JP": _babelRuntimeHelpersInteropRequireDefault($a72f8d03d4866b0b0942fc7364f6c17f$exports).default,
  "ko-KR": _babelRuntimeHelpersInteropRequireDefault($afa7d8f59ebcb29113d9b76f75d93$exports).default,
  "lt-LT": _babelRuntimeHelpersInteropRequireDefault($beac8373bccb98df7426400cbb42$exports).default,
  "lv-LV": _babelRuntimeHelpersInteropRequireDefault($c39fc9f76532e2d96005ff819ea6fb48$exports).default,
  "nb-NO": _babelRuntimeHelpersInteropRequireDefault($bd42e2aab422cdeb54266963268ab53$exports).default,
  "nl-NL": _babelRuntimeHelpersInteropRequireDefault($a755f8465ba08d876a6edc702f660c$exports).default,
  "pl-PL": _babelRuntimeHelpersInteropRequireDefault($fb7b2809312b948f135fc12bee25695$exports).default,
  "pt-BR": _babelRuntimeHelpersInteropRequireDefault($e0a153f32c1a45327d181dd0c500676$exports).default,
  "pt-PT": _babelRuntimeHelpersInteropRequireDefault($ad3816a8b2225b00c10542dd14ac37d3$exports).default,
  "ro-RO": _babelRuntimeHelpersInteropRequireDefault($f0b80e4973d410b36a72479b0fc90$exports).default,
  "ru-RU": _babelRuntimeHelpersInteropRequireDefault($cfda2747a3f6051c45ad9108a9a705$exports).default,
  "sk-SK": _babelRuntimeHelpersInteropRequireDefault($d339184d3b85ce4512ac28ce36ed4c$exports).default,
  "sl-SI": _babelRuntimeHelpersInteropRequireDefault($c26edd75631a1047e596d7f3a08ac91$exports).default,
  "sr-SP": _babelRuntimeHelpersInteropRequireDefault($ddc13676954a8ad8e15fb129afdfc207$exports).default,
  "sv-SE": _babelRuntimeHelpersInteropRequireDefault($f3e3a34a322ae9d9a02dc1fd1993bbe$exports).default,
  "tr-TR": _babelRuntimeHelpersInteropRequireDefault($e43dd3d8f81c5a7432b792e1f6f98055$exports).default,
  "uk-UA": _babelRuntimeHelpersInteropRequireDefault($c3b936950f1f3334d7b4d4f757af68e$exports).default,
  "zh-CN": _babelRuntimeHelpersInteropRequireDefault($d58d47f7a6e3477a53b4bae760321$exports).default,
  "zh-TW": _babelRuntimeHelpersInteropRequireDefault($bcd230dc1cabfc61b9e27d841f17b04$exports).default
};

/**
 * Provides the behavior and accessibility implementation for a selection checkbox in a table.
 * @param props - Props for the selection checkbox.
 * @param state - State of the table, as returned by `useTableState`.
 */
function useTableSelectionCheckbox(props, state) {
  let {
    key
  } = props;
  const {
    checkboxProps
  } = useGridSelectionCheckbox(props, state);
  return {
    checkboxProps: _babelRuntimeHelpersExtends({}, checkboxProps, {
      'aria-labelledby': checkboxProps.id + " " + $b62c6c69dd91ba8b8df7cfb5ba576$export$getRowLabelledBy(state, key)
    })
  };
}
/**
 * Provides the behavior and accessibility implementation for the select all checkbox in a table.
 * @param props - Props for the select all checkbox.
 * @param state - State of the table, as returned by `useTableState`.
 */


exports.useTableSelectionCheckbox = useTableSelectionCheckbox;

function useTableSelectAllCheckbox(state) {
  let {
    isEmpty,
    isSelectAll,
    selectionMode
  } = state.selectionManager;
  const formatMessage = useMessageFormatter($c82313c140cce815a7e91604cb62d626$var$intlMessages);
  return {
    checkboxProps: {
      'aria-label': formatMessage(selectionMode === 'single' ? 'select' : 'selectAll'),
      isSelected: isSelectAll,
      isDisabled: selectionMode !== 'multiple',
      isIndeterminate: !isEmpty && !isSelectAll,
      onChange: () => state.selectionManager.toggleSelectAll()
    }
  };
}

exports.useTableSelectAllCheckbox = useTableSelectAllCheckbox;
